/**
 * Stage copy cycled while a citation request is in `queued` or
 * `searching` state. Shared between the chat-bubble pill
 * (`AddReferencesPromptBubble`) and the per-statement progress rows in
 * the triage sheet's searching phase (`SearchingStatementRow`) so the
 * two surfaces narrate the same backend steps in the same words.
 *
 * Each rendered surface gets a five-stage sequence addressed to the
 * expert who requested the references. Stages map positionally:
 *
 *   1. Initial intake of the expert's statements
 *   2. Broad literature search
 *   3. Guideline lookup
 *   4. Trial-data cross-reference
 *   5. Ranking by strength of evidence
 *
 * The sequence is assembled at mount via `pickCitationSearchLabels` —
 * one line is picked independently from each stage's pool, so a run's
 * voicing varies across stages instead of being locked to a single
 * tonal set. Tone target: deferential, attentive, lightly witty.
 *
 * Stage 1 is one-shot — it only makes sense at the start of the run.
 * After the full pass the rotation cycles among the last
 * `CITATION_SEARCH_STICKY_TAIL_COUNT` entries so a long-running request
 * doesn't snap back to the intake line and feel like it's restarting
 * from scratch.
 */
export type CitationSearchLabelSet = readonly [string, string, string, string, string];

const CITATION_SEARCH_STAGE_POOLS = [
  // 1. Initial intake
  [
    'Got it — pulling up your statements',
    'Received with thanks — beginning the search',
    'Thank you — gathering your statements',
    'Received — opening the stacks',
    'Got it — listening for what you need',
    'Thanks — taking your statements in hand',
    "Thanks — we'll get right on it",
  ],
  // 2. Broad literature search
  [
    'Sweeping through the literature',
    'Searching the literature thoroughly',
    'Walking the literature, slowly',
    'Working through the literature',
    'Scanning the literature',
    'Moving through the literature with care',
    'Combing the literature',
  ],
  // 3. Guideline lookup
  [
    'Peeking at the guidelines',
    'Consulting the guidelines',
    'Reading the guidelines',
    'Pulling the guideline binders',
    'Checking the guidelines',
    'Reviewing the guidelines',
    'Flipping through the guidelines',
  ],
  // 4. Trial-data cross-reference
  [
    'Eyeing the trial data',
    'Examining the trial data',
    'Looking through the trial data',
    'Cross-referencing the trial data',
    'Sifting through the trial data',
    'Considering the trial data',
    'Browsing the trial data',
  ],
  // 5. Ranking by strength of evidence
  [
    'Lining up the strongest matches',
    'Ranking by strength of evidence',
    'Letting the strongest rise to the top',
    'Sorting by strength of evidence',
    'Stacking the strongest references',
    'Weighing the references',
    'Bringing the top matches forward',
  ],
] as const;

export const CITATION_SEARCH_STICKY_TAIL_COUNT = 4;

function pickFromPool(pool: readonly string[]): string {
  return pool[Math.floor(Math.random() * pool.length)];
}

/**
 * Assemble a five-stage citation-search label sequence by picking one
 * line independently from each stage's pool. Call once per mount
 * (stash in `useState`) so the chosen labels stay stable for the
 * lifetime of the surface — re-renders don't re-roll; a fresh request
 * gets a fresh roll for every stage.
 */
export function pickCitationSearchLabels(): CitationSearchLabelSet {
  return [
    pickFromPool(CITATION_SEARCH_STAGE_POOLS[0]),
    pickFromPool(CITATION_SEARCH_STAGE_POOLS[1]),
    pickFromPool(CITATION_SEARCH_STAGE_POOLS[2]),
    pickFromPool(CITATION_SEARCH_STAGE_POOLS[3]),
    pickFromPool(CITATION_SEARCH_STAGE_POOLS[4]),
  ];
}
